import * as React from "react";
import {
  Switch,
  Checkbox,
  Collapse,
  Row,
  Card,
  InputNumber,
  Col,
  Select,
  Radio,
  Tooltip,
  Space,
  Input,
  Form,
  FormInstance,
} from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ProjectTabProps } from "../Projects";
import { Loader } from "Components/General/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputWithMask } from "Components/General/InputWithMask";
import { MetaDataValueSelect } from "./MetaDataValueSelect";
import { OxModulesOptions } from "./OxModulesOptions";

interface OptionsTabProps extends ProjectTabProps, WithTranslation {
  functionsOptions: API_FunctionOptions;
  modulesOptions: API_ModuleOptions;
  modulesActive: { [key in API_MODULEDATA_MODULE]: boolean };
  functionsActive: { [key in API_PROJECT_FUNCTIONS]: boolean };
  groupsForRights: API_Group[];
  formRef: React.RefObject<FormInstance>;
}

interface OptionsTabState {
  showAllForModules: boolean;
  showAllForFunc: boolean;
  isMailFilePatternDisabled: boolean;
  defaultWatermark: boolean;
  qrCodeInZipDownload: boolean;
  automaticNumberingForFiles: boolean;
  automaticNumberingForDirectorys: boolean;
}
export const OptionsTab = withTranslation([I18N_NS.ADMIN, I18N_NS.PROJECT])(
  observer(
    class extends React.Component<OptionsTabProps, OptionsTabState> {
      private watermarkMask = [
        { mask: "@DATE", label: this.props.t("Date") },
        { mask: "@USERID", label: this.props.t("User id") },
        { mask: "@USERNAME", label: this.props.t("Username") },
      ];

      private mailFileNamePatternsMask = [
        { mask: "@DATE$YYMMDD", label: `${this.props.t("Date")} YYMMDD` },
        { mask: "@DATE$YYYYMMDD", label: `${this.props.t("Date")} YYYMMDD` },
        { mask: "@SUBJECT", label: this.props.t("Subject") },
        { mask: "@ID", label: this.props.t("Email id") },
        { mask: "@SENDER", label: this.props.t("Sender") },
      ];

      constructor(props: OptionsTabProps) {
        super(props);
        this.state = {
          showAllForModules: false,
          showAllForFunc: false,
          isMailFilePatternDisabled: true,
          defaultWatermark: false,
          qrCodeInZipDownload: false,
          automaticNumberingForFiles: false,
          automaticNumberingForDirectorys: false,
        };
      }

      componentDidMount() {
        if (this.props.functionsOptions && !this.props.functionsOptions.pdfConverterOptions.watermark) {
          this.setState({ defaultWatermark: true });
        }
        if (this.props.functionsOptions && this.props.functionsOptions.plancheckMobileOptions.qrCodeInZipDownload) {
          this.setState({ qrCodeInZipDownload: true });
        }
        if (this.props.modulesOptions) {
          this.setState({
            automaticNumberingForDirectorys: this.props.modulesOptions.automaticNumberingOptions.forDirectories,
            automaticNumberingForFiles: this.props.modulesOptions.automaticNumberingOptions.forFiles,
          });
        }
      }

      handleSwitchForFunc = () => {
        this.setState({ showAllForFunc: !this.state.showAllForFunc });
      };

      handleSwitchForModules = () => {
        this.setState({ showAllForModules: !this.state.showAllForModules });
      };

      handleWatermarks = (value: boolean) => {
        const { formRef } = this.props;

        this.setState({ defaultWatermark: value }, () => {
          if (this.state.defaultWatermark) {
            formRef.current.setFieldsValue({ functionOptions: { pdfConverterOptions: { watermark: null } } });
          } else {
            formRef.current.setFieldsValue({
              functionOptions: {
                pdfConverterOptions: {
                  watermark: formRef.current.getFieldValue("functionOptions.pdfConverterOptions.watermark"),
                },
              },
            });
          }
        });
      };

      handleQRChange = (value: boolean) => {
        const { formRef } = this.props;
        this.setState({ qrCodeInZipDownload: value }, () => {
          if (!this.state.qrCodeInZipDownload) {
            formRef.current.setFieldsValue({
              functionOptions: {
                plancheckMobileOptions: {
                  forceQr: false,
                },
              },
            });
          }
        });
      };

      handleAutomaticNumberingChange = () => {
        const { formRef } = this.props;
        if (!this.state.automaticNumberingForDirectorys || !this.state.automaticNumberingForFiles) {
          formRef.current.setFieldsValue({
            moduleOptions: {
              automaticNumberingOptions: {
                hasConsistentNumbering: false,
              },
            },
          });
        }
      };

      handleAutomaticNumberingForDirectorysChange = (value: boolean) => {
        this.setState({ automaticNumberingForDirectorys: value }, () => {
          this.handleAutomaticNumberingChange();
        });
      };

      handleAutomaticNumberingForFilesChange = (value: boolean) => {
        this.setState({ automaticNumberingForFiles: value }, () => {
          this.handleAutomaticNumberingChange();
        });
      };

      renderOxWarningIcon = () => {
        const {
          t,
          modulesActive: { ONLINEBAUABNAHME },
          modulesOptions: { oxSettings },
        } = this.props;
        // const isWarning = ONLINEBAUABNAHME && (!oxSettings.client || !oxSettings.project);
        const isWarning = ONLINEBAUABNAHME && !oxSettings.client;
        const message: string[] = [];

        // if (!oxSettings.project) {
        //   message.push(t("Please configure an OX project"));
        // }

        if (!oxSettings.client) {
          message.push(t("Please configure an OX client"));
        }

        return isWarning ? (
          <Tooltip
            title={message.map((v) => (
              <div key={v}>{v}</div>
            ))}
          >
            <FontAwesomeIcon
              fixedWidth={true}
              size="lg"
              className={"text-warning"}
              icon={["far", "exclamation-circle"]}
            />
          </Tooltip>
        ) : null;
      };

      render() {
        if (!this.props.modulesOptions && !this.props.functionsOptions) {
          return <Loader />;
        } else {
          const { showAllForModules, showAllForFunc, isMailFilePatternDisabled } = this.state;
          const {
            t,
            groupsForRights,
            formRef,
            modulesActive,
            functionsActive,
            functionsOptions: {
              pdfConverterOptions,
              easyViewerOptions,
              plancheckMobileOptions,
              officeViewerOptions,
              fileNameCheckOptions,
              labelOptions,
              bimViewerOptions,
              aiChatSettings,
            },
            modulesOptions: {
              participantsReportOptions,
              plotAndPrintOptions,
              rightsManagementOptions,
              workflowOptions,
              mailOptions,
              automaticNumberingOptions,
              oxSettings,
              docNodeOptions,
            },
          } = this.props;

          const EVHeader = () => (
            <Tooltip
              title={t(
                `Easy Viewer only sets watermarks if an individual watermark is configured. If in contrast, the standard watermark setting is chosen in the above “Watermarks” section, then no watermarks will be added by Easy Viewer.`
              )}
            >
              <Space>
                {t("Easy Viewer")}
                <FontAwesomeIcon icon={["far", "info-circle"]} color="blue" />
              </Space>
            </Tooltip>
          );

          return (
            <div>
              <Row justify="space-between">
                <h2>{t("For Modules")}</h2>
                <div className="mb-2">
                  <Switch onChange={this.handleSwitchForModules} checked={!showAllForModules} />
                  <span style={{ marginLeft: 5 }}>{t("Only show options of active modules")}</span>
                </div>
              </Row>
              <Card variant="outlined" title={t("Classic and Modern")}>
                <Collapse>
                  <Collapse.Panel key="1" header={t("Documents")} forceRender>
                    <Collapse bordered={false} className="mb-2">
                      <Collapse.Panel key="1" header={t("Rights management")} forceRender>
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasDirectoryRights"]}
                          initialValue={rightsManagementOptions.hasDirectoryRights}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                          getValueFromEvent={(e) => {
                            const childValue = formRef.current.getFieldValue(
                              "moduleOptions.rightsManagementOptions.hasSpecialRights"
                            );
                            if (!e.target.checked && childValue) {
                              formRef.current.setFieldsValue({
                                moduleOptions: { rightsManagementOptions: { hasSpecialRights: false } },
                              });
                              return e.target.checked;
                            } else {
                              return e.target.checked;
                            }
                          }}
                        >
                          <Checkbox>{t("Directory rights")}</Checkbox>
                        </Form.Item>
                        {/* <div style={{ marginLeft: '25px' }}> 	DEV-3415 remove option
                        {getFieldDecorator('moduleOptions.rightsManagementOptions.hasSpecialRights', {
                          initialValue: rightsManagementOptions.hasSpecialRights,
                          valuePropName: 'checked',
                        })(
                          <Checkbox
                            disabled={!getFieldValue('moduleOptions.rightsManagementOptions.hasDirectoryRights')}
                          >
                            {t('Special rights (view only and owner)')}
                          </Checkbox>
                        )}
                      </div> */}
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasRightsChangeFileName"]}
                          initialValue={rightsManagementOptions.hasRightsChangeFileName}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Allow to change file name (only owners)")}</Checkbox>
                        </Form.Item>
                        {/* <div>
                        {getFieldDecorator('moduleOptions.rightsManagementOptions.hasRightsChangeFileDescription', {
                          initialValue: rightsManagementOptions.hasRightsChangeFileDescription,
                          valuePropName: 'checked',
                        })(
                          <Checkbox>{t('Allow to change file description (for all user with write access)')}</Checkbox>
                        )}
                      </div> */}
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasOnlyAdminAccessMoveFiles"]}
                          initialValue={rightsManagementOptions.hasOnlyAdminAccessMoveFiles}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Only project admins can move files (not owners)")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasAccessLockUnlockFiles"]}
                          initialValue={rightsManagementOptions.hasAccessLockUnlockFiles}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Lock and unlock files")}</Checkbox>
                        </Form.Item>
                      </Collapse.Panel>

                      <Collapse.Panel key="2" header={t("Automatic numbering")} forceRender>
                        <Form.Item
                          name={["moduleOptions", "automaticNumberingOptions", "forDirectories"]}
                          initialValue={automaticNumberingOptions.forDirectories}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox
                            onChange={(e) => this.handleAutomaticNumberingForDirectorysChange(e.target.checked)}
                          >
                            {t("For directories")}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "automaticNumberingOptions", "forFiles"]}
                          initialValue={automaticNumberingOptions.forFiles}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox onChange={(e) => this.handleAutomaticNumberingForFilesChange(e.target.checked)}>
                            {t("For Files")}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          style={{ marginLeft: "25px" }}
                          name={["moduleOptions", "automaticNumberingOptions", "hasConsistentNumbering"]}
                          initialValue={automaticNumberingOptions.hasConsistentNumbering}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox
                            disabled={
                              !this.state.automaticNumberingForDirectorys || !this.state.automaticNumberingForFiles
                            }
                          >
                            {t(
                              `Include directory number as prefix (z.B. "01.01.01 file" for first file in directory "01.01")`
                            )}
                          </Checkbox>
                        </Form.Item>
                      </Collapse.Panel>

                      <Collapse.Panel key="3" header={t("Plot and print")} forceRender>
                        <Form.Item
                          name={["moduleOptions", "plotAndPrintOptions", "showingAlternativePlotServices"]}
                          initialValue={plotAndPrintOptions.showingAlternativePlotServices}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Allow alternative repro service providers")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "plotAndPrintOptions", "notShowingCDs"]}
                          initialValue={plotAndPrintOptions.notShowingCDs}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Do not show CDS as repro service provider")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "plotAndPrintOptions", "hasWarningMessage"]}
                          initialValue={plotAndPrintOptions.hasWarningMessage}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Warn the user if a file has already been printed (SWM function)")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "plotAndPrintOptions", "documentStatusBelowQRCode"]}
                          initialValue={plotAndPrintOptions.documentStatusBelowQRCode}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Put document status below QR-code")}</Checkbox>
                        </Form.Item>
                      </Collapse.Panel>
                    </Collapse>
                  </Collapse.Panel>

                  {(showAllForModules || modulesActive.WORKFLOW) && (
                    <Collapse.Panel key="2" header={t("Workflow")} forceRender>
                      <Row gutter={20}>
                        <Col md={12}>
                          <Form.Item
                            colon={true}
                            label={t("Allowed for workflow administration")}
                            name={["moduleOptions", "workflowOptions", "administrationGroupId"]}
                            initialValue={workflowOptions.administrationGroupId}
                          >
                            <Select allowClear={true} showSearch={true} optionFilterProp="children">
                              {groupsForRights.map((prGp) => {
                                return prGp.id !== -108 ? (
                                  <Select.Option key={`${prGp.id}`} value={prGp.id}>
                                    {prGp.name}
                                  </Select.Option>
                                ) : null;
                              })}
                              <Select.Option key={`-1`} value={-1}>
                                {t(`${I18N_NS.PROJECT}:::Disabled`)}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col md={12}>
                          <Form.Item
                            colon={true}
                            label={t("Extended search for group")}
                            name={["moduleOptions", "workflowOptions", "searchForGroupId"]}
                            initialValue={workflowOptions.searchForGroupId}
                          >
                            <Select allowClear={true} showSearch={true} optionFilterProp="children">
                              {groupsForRights.map((prGp) => {
                                return prGp.id !== -108 ? (
                                  <Select.Option key={`${prGp.id}`} value={prGp.id}>
                                    {prGp.name}
                                  </Select.Option>
                                ) : null;
                              })}
                              <Select.Option key={`-1`} value={-1}>
                                {t(`${I18N_NS.PROJECT}:::Disabled`)}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        name={["moduleOptions", "workflowOptions", "showContentInDetails"]}
                        initialValue={workflowOptions.showContentInDetails}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Show content of workflow fields in workflow details")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["moduleOptions", "workflowOptions", "separationForFileUpload"]}
                        initialValue={workflowOptions.separationForFileUpload}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Start separate workflows for file upload")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["moduleOptions", "workflowOptions", "valuesManageable"]}
                        initialValue={workflowOptions.valuesManageable}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Tables of values can be managed")}</Checkbox>
                      </Form.Item>
                    </Collapse.Panel>
                  )}
                  {(showAllForModules || modulesActive.MAIL) && (
                    <Collapse.Panel key="3" header={t("Communications")} forceRender>
                      <Form.Item
                        colon={true}
                        label={t("File name pattern for moving emails")}
                        name={["moduleOptions", "mailOptions", "mailFilePattern"]}
                        initialValue={mailOptions.mailFilePattern}
                      >
                        <InputWithMask
                          maskConfig={this.mailFileNamePatternsMask}
                          disabled={isMailFilePatternDisabled}
                          suffix={
                            // tslint:disable-next-line:max-line-length
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                isMailFilePatternDisabled
                                  ? this.setState({ isMailFilePatternDisabled: !isMailFilePatternDisabled })
                                  : undefined
                              }
                            >
                              <FontAwesomeIcon icon={"edit"} />
                            </span>
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={["moduleOptions", "mailOptions", "allowZipToExternals"]}
                        initialValue={mailOptions.allowZipToExternals}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Emails to externals may have real attachments (always zip)")}</Checkbox>
                      </Form.Item>
                    </Collapse.Panel>
                  )}
                </Collapse>
              </Card>
              <Card variant="outlined" title={t("Purely Classic")}>
                <Collapse>
                  <Collapse.Panel key="1" header={t("Documents")} forceRender>
                    <Collapse bordered={false} className="mb-2">
                      <Collapse.Panel key="1" header={t("Rights management")} forceRender>
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasPrivateTrash"]}
                          initialValue={rightsManagementOptions.hasPrivateTrash}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Private trash")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "rightsManagementOptions", "hasDeleteCaution"]}
                          initialValue={rightsManagementOptions.hasDeleteCaution}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Real delete (use with caution)")}</Checkbox>
                        </Form.Item>
                      </Collapse.Panel>

                      <Collapse.Panel key="2" header={t("Participants report")} forceRender>
                        <Form.Item
                          name={["moduleOptions", "participantsReportOptions", "hidingPossibility"]}
                          initialValue={participantsReportOptions.hidingPossibility}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Hide user -type and -name in report")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "participantsReportOptions", "showingDepartmentInfo"]}
                          initialValue={participantsReportOptions.showingDepartmentInfo}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Show department information in report")}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={["moduleOptions", "participantsReportOptions", "showingLogo"]}
                          initialValue={participantsReportOptions.showingLogo}
                          valuePropName="checked"
                          className="ant-form-item-with-no-height"
                        >
                          <Checkbox>{t("Show project logo in report")}</Checkbox>
                        </Form.Item>
                      </Collapse.Panel>
                    </Collapse>
                  </Collapse.Panel>
                </Collapse>
              </Card>
              <Card variant="outlined" title={t("Purely Modern")}>
                <Collapse>
                  <Collapse.Panel key="1" header={t("OX modules")} extra={this.renderOxWarningIcon()} forceRender>
                    <Row gutter={20}>
                      <Col md={24}>
                        <Form.Item
                          colon={true}
                          label={t("OX client")}
                          name={["moduleOptions", "oxSettings", "client"]}
                          initialValue={oxSettings.client}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col md={24}>
                        <Form.Item
                          colon={true}
                          label={t("OX projects")}
                          name={["moduleOptions", "oxSettings", "oxProjects"]}
                          initialValue={oxSettings.oxProjects}
                        >
                          <OxModulesOptions />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Collapse.Panel>
                  <Collapse.Panel key="2" header={t("Docnodes-Blockchain")}>
                    <Form.Item
                      colon={true}
                      label={t("API Token")}
                      name={["moduleOptions", "docNodeOptions", "apiToken"]}
                      initialValue={docNodeOptions.apiToken}
                    >
                      <Input />
                    </Form.Item>
                  </Collapse.Panel>
                </Collapse>
              </Card>

              <Row justify="space-between">
                <h2>{t("For Functions")}</h2>
                <div className="mb-2">
                  <Switch onChange={this.handleSwitchForFunc} checked={!showAllForFunc} />
                  <span style={{ marginLeft: 5 }}>{t("Only show options of active functions")}</span>
                </div>
              </Row>
              <Card variant="outlined" title={t("Classic and Modern")}>
                {/* TODO: Enable in future */}
                {/* <Col span={8} style={{ textAlign: 'center' }}>
                      <Popconfirm
                        title={t('WARNING: resource-expensive operation! Are you sure?')}
                        okText={t('Build')}
                        cancelText={t('Cancel')}
                      >
                        <Button
                          type="primary"
                        >
                          {t('Build PDF for existing files')}
                        </Button>
                      </Popconfirm>
                    </Col> */}
                <Collapse className="mb-2">
                  <Collapse.Panel key="1" header={t("Watermarks")} forceRender>
                    <Radio.Group
                      style={{ width: "100%" }}
                      onChange={(e) => this.handleWatermarks(e.target.value)}
                      value={this.state.defaultWatermark}
                    >
                      <Row align="top" justify="space-between" className="mb-2">
                        <Col span={10}>
                          <Col>
                            <Radio className="mb-2" value={true}>
                              {t("Use default watermark")}
                            </Radio>
                          </Col>
                          <Col span={24}>
                            {t("Current standard value")}
                            {`: ${pdfConverterOptions.defaultWatermark}`}
                          </Col>
                        </Col>
                        <Col span={10}>
                          <Col>
                            <Radio className="mb-2" value={false}>
                              {t("Use individual configuration")}
                            </Radio>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={["functionOptions", "pdfConverterOptions", "watermark"]}
                              initialValue={pdfConverterOptions.watermark ? pdfConverterOptions.watermark : ""}
                            >
                              <InputWithMask
                                disabled={this.state.defaultWatermark}
                                maskConfig={this.watermarkMask}
                                size="small"
                              />
                            </Form.Item>
                          </Col>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Collapse.Panel>
                  {(showAllForFunc || functionsActive.PDFCONVERTATION) && (
                    <Collapse.Panel key="2" header={t("PDF conversion")} forceRender>
                      <Form.Item
                        className="mb-2 ant-form-item-with-no-height"
                        name={["functionOptions", "pdfConverterOptions", "hasOnlyViewRights"]}
                        initialValue={pdfConverterOptions.hasOnlyViewRights}
                        valuePropName="checked"
                      >
                        <Checkbox>{t('PDF download with just "Only view" right')}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        className="mb-2 ant-form-item-with-no-height"
                        name={["functionOptions", "pdfConverterOptions", "enableWatermarks"]}
                        initialValue={pdfConverterOptions.enableWatermarks}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Set watermarks")}</Checkbox>
                      </Form.Item>
                    </Collapse.Panel>
                  )}

                  {(showAllForFunc || functionsActive.EASYVIEWER) && (
                    <Collapse.Panel key="3" header={EVHeader()} forceRender>
                      <Form.Item
                        name={["functionOptions", "easyViewerOptions", "fullVersion"]}
                        initialValue={easyViewerOptions.fullVersion}
                        valuePropName="checked"
                        className="ant-form-item-with-no-height"
                      >
                        <Checkbox>{t("Enable full version")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "easyViewerOptions", "hasCache"]}
                        initialValue={easyViewerOptions.hasCache}
                        valuePropName="checked"
                        className="ant-form-item-with-no-height"
                      >
                        <Checkbox>{t("Activate Easy Viewer cache")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "easyViewerOptions", "hasNotes"]}
                        initialValue={easyViewerOptions.hasNotes}
                        valuePropName="checked"
                        className="ant-form-item-with-no-height"
                      >
                        <Checkbox>{t("Activate Easy Viewer notes")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        className="mb-2 ant-form-item-with-no-height"
                        style={{ paddingLeft: "25px" }}
                        name={["functionOptions", "easyViewerOptions", "pageWidth"]}
                        initialValue={easyViewerOptions.pageWidth}
                        label={<span style={{ marginRight: "10px" }}>{t("Page width (pixel)")}</span>}
                      >
                        <InputNumber size="small" min={1} />
                      </Form.Item>
                    </Collapse.Panel>
                  )}
                  {(showAllForFunc || functionsActive.FILECHECK) && (
                    <Collapse.Panel key="4" header={t("File Check Mobile")} forceRender>
                      <div>
                        <div style={{ marginRight: "10px" }}>{t("Position of the QR code:")}</div>
                        <Row align="top" justify="start" className="mb-2">
                          <Col span={5}>
                            <Form.Item
                              name={["functionOptions", "plancheckMobileOptions", "qrPosx"]}
                              initialValue={plancheckMobileOptions.qrPosx}
                              label={t("Offset in mm from the right ")}
                            >
                              <InputNumber size="small" />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Form.Item
                              name={["functionOptions", "plancheckMobileOptions", "qrPosY"]}
                              initialValue={plancheckMobileOptions.qrPosY}
                              label={t("and bottom edge.")}
                            >
                              <InputNumber size="small" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                      {(showAllForFunc || functionsActive.METADATA) && (
                        <div>
                          {t("Metadata filter:")}
                          <MetaDataValueSelect
                            metadataName={plancheckMobileOptions.qrFilterMD}
                            metadataValue={plancheckMobileOptions.qrFilterValue}
                            metadataToShow={plancheckMobileOptions.qrMDtoShow}
                            formRef={this.props.formRef}
                          />
                        </div>
                      )}
                      <Form.Item
                        name={["functionOptions", "plancheckMobileOptions", "qrOnlyWithDummy"]}
                        initialValue={plancheckMobileOptions.qrOnlyWithDummy}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Only add QR code, if a Dummy QR is available")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "plancheckMobileOptions", "qrCodeInPdf"]}
                        initialValue={plancheckMobileOptions.qrCodeInPdf}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("QR code in print jobs")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "plancheckMobileOptions", "qrCodeInZipDownload"]}
                        initialValue={plancheckMobileOptions.qrCodeInZipDownload}
                        valuePropName="checked"
                      >
                        <Checkbox onChange={(e) => this.handleQRChange(e.target.checked)}>
                          {t("Provide possibility to add QR code in download wizard")}
                        </Checkbox>
                      </Form.Item>
                      <Form.Item
                        style={{ marginLeft: "10px" }}
                        name={["functionOptions", "plancheckMobileOptions", "forceQr"]}
                        initialValue={plancheckMobileOptions.forceQr}
                        valuePropName="checked"
                      >
                        <Checkbox disabled={!this.state.qrCodeInZipDownload}>
                          {t("Add QR code to downloads by default")}
                        </Checkbox>
                      </Form.Item>
                    </Collapse.Panel>
                  )}
                </Collapse>
              </Card>
              <Card variant="outlined" title={t("Purely Modern")}>
                <Collapse>
                  <Collapse.Panel key="1" header={"Microsoft Office Online"} forceRender>
                    <Form.Item
                      name={["functionOptions", "officeViewerOptions", "editMode"]}
                      initialValue={officeViewerOptions.editMode}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("Enable edit mode")}</Checkbox>
                    </Form.Item>
                  </Collapse.Panel>
                  <Collapse.Panel key="bim" header={"BIM Viewer"} forceRender>
                    <Form.Item
                      name={["functionOptions", "bimViewerOptions", "fullMode"]}
                      initialValue={bimViewerOptions.fullMode}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("Enable full version")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name={["functionOptions", "bimViewerOptions", "bcfServer"]}
                      initialValue={bimViewerOptions.bcfServer}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("BCF Server enable")}</Checkbox>
                    </Form.Item>
                  </Collapse.Panel>
                  <Collapse.Panel key="2" header={t("Filename convention")} forceRender>
                    <Form.Item
                      name={["functionOptions", "fileNameCheckOptions", "filenameAssistant"]}
                      initialValue={fileNameCheckOptions.filenameAssistant}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("Filename assistant")}</Checkbox>
                    </Form.Item>
                  </Collapse.Panel>
                  <Collapse.Panel key="3" header={t("Labels")} forceRender>
                    <Form.Item
                      name={["functionOptions", "labelOptions", "folderLabels"]}
                      initialValue={labelOptions.folderLabels}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("Folder labels (label calculator)")}</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name={["functionOptions", "labelOptions", "automaticLabels"]}
                      initialValue={labelOptions.automaticLabels}
                      valuePropName="checked"
                      className="ant-form-item-with-no-height"
                    >
                      <Checkbox>{t("Automatic labels")}</Checkbox>
                    </Form.Item>
                  </Collapse.Panel>
                  {(showAllForFunc || functionsActive.AI_CHAT) && (
                    <Collapse.Panel key="AI" header={t("AI")} forceRender>
                      <Form.Item
                        name={["functionOptions", "aiChatSettings", "aiChatAsSettings"]}
                        initialValue={aiChatSettings.aiChatAsSettings}
                        valuePropName="checked"
                      >
                        <Checkbox>{t("Managed from project settings")}</Checkbox>
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "aiChatSettings", "baseUrl"]}
                        initialValue={aiChatSettings.baseUrl}
                        label={t("Base URL")}
                      >
                        <Input placeholder={t("Base URL")} />
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "aiChatSettings", "apiKey"]}
                        initialValue={aiChatSettings.apiKey}
                        label={t("API Key")}
                      >
                        <Input placeholder={t("API key")} />
                      </Form.Item>
                      <Form.Item
                        name={["functionOptions", "aiChatSettings", "model"]}
                        initialValue={aiChatSettings.model}
                        label={t("Model")}
                      >
                        <Input placeholder={t("Model")} />
                      </Form.Item>
                    </Collapse.Panel>
                  )}
                </Collapse>
              </Card>
            </div>
          );
        }
      }
    }
  )
);
